import React, { useCallback, useRef } from "react";
import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { Persistor } from "redux-persist";
import { useQueryClient } from "react-query";

import { useJwtExpiry } from "../hooks/useJwtExpiry";
import useRefreshAuthMutation from "../hooks/useRefreshAuth";

interface JwtProviderProps {
  children: React.ReactNode;
  tokenSelector: (state: any) => { token: string; userId: string };
  setTokenAction: ActionCreatorWithPayload<{ token: string }, string>;
  persistor?: Persistor;
  resetActions?: Array<() => { type: string }>;
}

const JwtProviderComponent: React.FC<JwtProviderProps> = ({
  children,
  tokenSelector,
  setTokenAction,
  persistor,
  resetActions,
}) => {
  const queryClient = useQueryClient();
  const refreshAuthMutation = useRefreshAuthMutation(setTokenAction);
  const { token, userId } = useSelector(tokenSelector);

  // Use refs to keep the latest values without causing handleTokenRefresh to change.
  // useMutation returns a new object every render, which would otherwise make
  // handleTokenRefresh unstable and cause the token timer to reset on every render.
  const mutationRef = useRef(refreshAuthMutation);
  mutationRef.current = refreshAuthMutation;

  const tokenRef = useRef(token);
  tokenRef.current = token;

  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const handleTokenRefresh = useCallback(async (): Promise<string | undefined> => {
    const result = await mutationRef.current.mutateAsync({
      token: tokenRef.current,
      userId: userIdRef.current,
    });
    return result?.expiresIn;
  }, []);

  useJwtExpiry(
    tokenSelector,
    handleTokenRefresh,
    persistor,
    queryClient,
    resetActions
  );
  return <>{children}</>;
};

// Memoize to prevent unnecessary remounts during navigation
export const JwtProvider = React.memo(JwtProviderComponent);
